import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Plus, FileText, Search, X, CheckCircle2,
  Calendar, AlertCircle, ChevronRight, Archive, RotateCw, Trash2,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { INCOME_SOURCES, DEDUCTION_SOURCES } from "./fiscal/catalogs";
import { API, authH } from "@/api";


const CURRENT_YEAR = new Date().getFullYear();

/**
 * Étape 4 — Liste des dossiers fiscaux.
 * Lit GET /api/fiscal-dossiers et affiche en tableau.
 * Bouton "+ Nouveau dossier fiscal" → wizard 2 étapes (sélection Master → champs fiscaux).
 */
export default function FiscalDossiersList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [mastersById, setMastersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState("");
  const [archivedFilter, setArchivedFilter] = useState(false);
  const [search, setSearch] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  // Batch #14.128 — Actions sur tuiles (archive / restore / hard delete)
  const [actioningId, setActioningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { archived: archivedFilter };
      if (yearFilter) params.tax_year = yearFilter;
      const r = await axios.get(`${API}/fiscal-dossiers`, { headers: authH(), params });
      const list = r.data?.items || [];
      setItems(list);
      // Charge les Masters en bulk (1 GET par Master unique)
      const uniqIds = [...new Set(list.map((d) => d.master_id))];
      const mb = {};
      await Promise.all(uniqIds.map(async (mid) => {
        try {
          const mr = await axios.get(`${API}/masters/${mid}`, { headers: authH() });
          mb[mid] = mr.data;
        } catch { /* skip */ }
      }));
      setMastersById(mb);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [yearFilter, archivedFilter]);

  useEffect(() => { load(); }, [load]);

  // Batch #14.128 — Archive un dossier fiscal actif
  const handleArchive = async (it, masterName) => {
    if (!window.confirm(
      `Archiver le dossier fiscal ${it.tax_year} de "${masterName}" ?\n\n` +
      `Il sera caché de la liste active. Réactivable depuis l'onglet "Voir archives".`
    )) return;
    setActioningId(it.id);
    try {
      await axios.delete(`${API}/fiscal-dossiers/${it.id}`, { headers: authH() });
      toast.success("Dossier fiscal archivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec d'archivage");
    } finally {
      setActioningId(null);
    }
  };

  // Batch #14.128 — Restaure un dossier fiscal archivé
  const handleRestore = async (it, masterName) => {
    if (!window.confirm(`Réactiver le dossier fiscal ${it.tax_year} de "${masterName}" ?`)) return;
    setActioningId(it.id);
    try {
      await axios.post(`${API}/fiscal-dossiers/${it.id}/restore`, {}, { headers: authH() });
      toast.success("Dossier fiscal réactivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de réactivation");
    } finally {
      setActioningId(null);
    }
  };

  // Batch #14.128 — Suppression DÉFINITIVE (depuis archives uniquement)
  const handleHardDelete = async (it, masterName) => {
    if (!window.confirm(
      `⚠️ SUPPRIMER DÉFINITIVEMENT le dossier fiscal ${it.tax_year} de "${masterName}" ?\n\n` +
      `Cette action est IRRÉVERSIBLE. Le dossier sera retiré de la base de données.\n\n` +
      `Le Master n'est PAS touché.`
    )) return;
    setActioningId(it.id);
    try {
      await axios.delete(
        `${API}/fiscal-dossiers/${it.id}?hard=true&confirm=SUPPRIMER`,
        { headers: authH() },
      );
      toast.success("Dossier fiscal supprimé définitivement");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de suppression");
    } finally {
      setActioningId(null);
    }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const m = mastersById[it.master_id];
    const name = (m?.couple_display_name || "").toLowerCase();
    return name.includes(search.toLowerCase()) || it.id.includes(search);
  });

  const years = [...new Set(items.map((d) => d.tax_year))].sort((a, b) => b - a);

  return (
    <div className="space-y-4" data-testid="fiscal-dossiers-list">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            <FileText className="w-6 h-6 text-blue-600" /> Fiscal
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Dossiers fiscaux liés à un Master. {items.length} dossier{items.length > 1 ? "s" : ""} {archivedFilter ? "archivé(s)" : "actif(s)"}.
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 inline-flex items-center gap-2"
          data-testid="fiscal-list-new-btn"
        >
          <Plus className="w-4 h-4" /> Nouveau dossier fiscal
        </button>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom de Master…"
            className="pl-9 pr-3 py-2 border rounded-lg text-sm w-72"
            data-testid="fiscal-list-search"
          />
        </div>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
          data-testid="fiscal-list-year-filter"
        >
          <option value="">Toutes années</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={archivedFilter}
            onChange={(e) => setArchivedFilter(e.target.checked)}
            data-testid="fiscal-list-archived-toggle"
          />
          Voir archives
        </label>
      </div>

      {/* Tuiles (Batch #14.65 — vue tuile remplace la vue liste) */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl text-center py-16">
          <Loader2 className="inline w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl text-center py-16" data-testid="fiscal-list-empty">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500 mb-1">Aucun dossier fiscal {archivedFilter ? "archivé" : "actif"}</p>
          <p className="text-xs text-slate-400">
            Cliquez sur "+ Nouveau dossier fiscal" pour créer le premier dossier.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"
          data-testid="fiscal-list-tiles"
        >
          {filtered.map((it) => {
            const m = mastersById[it.master_id];
            const masterName = m?.couple_display_name || "(master inconnu)";
            const isArchived = !!it.is_archived;
            const isBusy = actioningId === it.id;

            return (
              <div
                key={it.id}
                className={`group relative bg-white border border-slate-200 rounded-xl p-4 transition-all duration-150 ${
                  isArchived
                    ? "opacity-80 hover:border-amber-300"
                    : "hover:border-blue-400 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
                }`}
                onClick={() => {
                  if (!isArchived) navigate(`/admin/fiscal-dossiers/${it.id}`);
                }}
                data-testid={`fiscal-list-tile-${it.id}`}
              >
                {/* Badge ARCHIVÉ en coin */}
                {isArchived && (
                  <span
                    className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-bold tracking-wider"
                    data-testid={`fiscal-list-archived-badge-${it.id}`}
                  >
                    ARCHIVÉ
                  </span>
                )}

                {/* En-tête : icône + année */}
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className={`text-right ${isArchived ? "mr-12" : ""}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Année</div>
                    <div className="text-xl font-mono font-bold text-slate-800 leading-none">{it.tax_year}</div>
                  </div>
                </div>

                {/* Master */}
                <div className="font-semibold text-slate-800 text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
                  {m?.couple_display_name || <span className="text-slate-400 italic">(master inconnu)</span>}
                </div>

                {/* État civil */}
                {it.marital_status && (
                  <div className="text-xs text-slate-500 mt-1 truncate">{it.marital_status}</div>
                )}

                {/* Statuts */}
                <div className="flex gap-1 flex-wrap mt-3 min-h-[22px]">
                  {it.last_declaration_filed && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Remise
                    </span>
                  )}
                  {it.current_declaration_in_progress && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      En cours
                    </span>
                  )}
                </div>

                {/* Pied : date + actions */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <div className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Calendar className="w-3 h-3" /> {new Date(it.created_at).toLocaleDateString("fr-CA")}
                  </div>

                  {/* Batch #14.128 — Actions selon état */}
                  {isArchived ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRestore(it, masterName); }}
                        disabled={isBusy}
                        className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 p-1 rounded inline-flex items-center gap-1 text-[11px] font-medium"
                        data-testid={`fiscal-list-restore-btn-${it.id}`}
                        title="Réactiver ce dossier fiscal"
                      >
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                        Restaurer
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleHardDelete(it, masterName); }}
                        disabled={isBusy}
                        className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 disabled:opacity-50 p-1 rounded inline-flex items-center gap-1 text-[11px] font-medium"
                        data-testid={`fiscal-list-delete-btn-${it.id}`}
                        title="Supprimer définitivement (irréversible)"
                      >
                        <Trash2 className="w-3 h-3" />
                        Supprimer
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleArchive(it, masterName); }}
                        disabled={isBusy}
                        className="text-amber-600 hover:text-amber-800 hover:bg-amber-50 disabled:opacity-50 p-1 rounded"
                        data-testid={`fiscal-list-archive-btn-${it.id}`}
                        title="Archiver ce dossier (le cacher de la liste)"
                      >
                        {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showWizard && (
        <CreateFiscalDossierWizard
          onClose={() => setShowWizard(false)}
          onCreated={(newId) => {
            setShowWizard(false);
            navigate(`/admin/fiscal-dossiers/${newId}`);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
//  Wizard 2 étapes — création d'un dossier fiscal
// ============================================================
function CreateFiscalDossierWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [masters, setMasters] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedMasterId, setSelectedMasterId] = useState("");
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tax_year: CURRENT_YEAR,
    sin_primary: "",
    sin_secondary: "",
    marital_status: "",
    income_sources: [],
    deduction_sources: [],
    last_declaration_filed: false,
    current_declaration_in_progress: true,
    notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/masters`, { headers: authH() });
        setMasters(r.data?.items || []);
      } catch {
        setMasters([]);
      } finally {
        setLoadingMasters(false);
      }
    })();
  }, []);

  const selectedMaster = masters.find((m) => m.id === selectedMasterId);
  const filtered = masters.filter((m) => !search ||
    (m.couple_display_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (m.primary_email || "").toLowerCase().includes(search.toLowerCase()));

  const toggleArr = (key, value) => {
    setForm((f) => {
      const arr = new Set(f[key] || []);
      if (arr.has(value)) arr.delete(value); else arr.add(value);
      return { ...f, [key]: Array.from(arr) };
    });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        master_id: selectedMasterId,
        tax_year: parseInt(form.tax_year, 10) || CURRENT_YEAR,
        sin_primary: form.sin_primary || null,
        sin_secondary: form.sin_secondary || null,
        marital_status: form.marital_status || null,
        income_sources: form.income_sources,
        deduction_sources: form.deduction_sources,
        last_declaration_filed: form.last_declaration_filed,
        current_declaration_in_progress: form.current_declaration_in_progress,
        notes: form.notes || null,
      };
      const r = await axios.post(`${API}/fiscal-dossiers`, payload, { headers: authH() });
      toast.success("Dossier fiscal créé");
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="fiscal-wizard-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div>
            <h3 className="font-bold">
              {step === 1 ? "Nouveau dossier fiscal — Étape 1/2" : "Nouveau dossier fiscal — Étape 2/2"}
            </h3>
            <p className="text-[11px] text-slate-500">
              {step === 1
                ? "Sélectionnez le Master à rattacher (NAS et coordonnées synchronisés)."
                : `Master sélectionné : ${selectedMaster?.couple_display_name || "?"}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded" data-testid="fiscal-wizard-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {step === 1 && (
          <>
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un Master par nom ou courriel…"
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                  data-testid="fiscal-wizard-master-search"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 px-2 py-1" data-testid="fiscal-wizard-master-list">
              {loadingMasters ? (
                <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400 italic">
                  Aucun Master trouvé. <Link to="/admin/masters" className="text-blue-600 underline">Créer un Master</Link>
                </div>
              ) : (
                filtered.map((m) => (
                  <label
                    key={m.id}
                    onClick={() => setSelectedMasterId(m.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${selectedMasterId === m.id ? "bg-blue-50" : ""}`}
                    data-testid={`fiscal-wizard-master-${m.id}`}
                  >
                    <input
                      type="radio"
                      name="master-pick"
                      checked={selectedMasterId === m.id}
                      onChange={() => setSelectedMasterId(m.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 text-sm truncate">{m.couple_display_name || "(sans nom)"}</div>
                      <div className="text-[11px] text-slate-500">{m.primary_name} {m.secondary_name ? `+ ${m.secondary_name}` : ""}{m.primary_email ? ` · ${m.primary_email}` : ""}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedMasterId}
                className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="fiscal-wizard-next"
              >
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
              {/* Bandeau Master sélectionné */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                <div className="font-semibold text-slate-700">{selectedMaster?.couple_display_name}</div>
                <div className="text-slate-500 mt-0.5">
                  {selectedMaster?.primary_name}
                  {selectedMaster?.secondary_name ? ` + ${selectedMaster.secondary_name}` : ""}
                </div>
                <div className="text-[10px] text-slate-400 italic mt-1 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Les champs ci-dessous appartiennent au dossier fiscal — pas au Master.
                </div>
              </div>

              {/* Année fiscale */}
              <label className="block text-sm">
                <span className="text-xs font-semibold text-slate-700">Année fiscale *</span>
                <input
                  type="number"
                  min="2000"
                  max="2099"
                  value={form.tax_year}
                  onChange={(e) => setForm({ ...form, tax_year: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg font-mono"
                  data-testid="fiscal-wizard-tax-year"
                />
              </label>

              {/* État civil */}
              <label className="block text-sm">
                <span className="text-xs font-semibold text-slate-700">État civil</span>
                <input
                  type="text"
                  value={form.marital_status}
                  onChange={(e) => setForm({ ...form, marital_status: e.target.value })}
                  maxLength={100}
                  placeholder="ex: marié, union de fait, célibataire…"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="fiscal-wizard-marital-status"
                />
              </label>

              {/* NAS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-slate-700">NAS Principal</span>
                  <input
                    type="text"
                    value={form.sin_primary}
                    onChange={(e) => setForm({ ...form, sin_primary: e.target.value })}
                    maxLength={20}
                    placeholder="XXX-XXX-XXX"
                    className="w-full mt-1 px-3 py-2 border rounded-lg font-mono"
                    data-testid="fiscal-wizard-sin-primary"
                  />
                </label>
                {selectedMaster?.secondary_user_id && (
                  <label className="block text-sm">
                    <span className="text-xs font-semibold text-slate-700">NAS Secondaire</span>
                    <input
                      type="text"
                      value={form.sin_secondary}
                      onChange={(e) => setForm({ ...form, sin_secondary: e.target.value })}
                      maxLength={20}
                      placeholder="XXX-XXX-XXX"
                      className="w-full mt-1 px-3 py-2 border rounded-lg font-mono"
                      data-testid="fiscal-wizard-sin-secondary"
                    />
                  </label>
                )}
              </div>

              {/* Sources revenus */}
              <WizardCheckboxGrid
                title="Sources de revenu"
                items={INCOME_SOURCES}
                selected={form.income_sources}
                onToggle={(id) => toggleArr("income_sources", id)}
                testidPrefix="fiscal-wizard-income"
              />

              {/* Sources déductions */}
              <WizardCheckboxGrid
                title="Sources de déduction"
                items={DEDUCTION_SOURCES}
                selected={form.deduction_sources}
                onToggle={(id) => toggleArr("deduction_sources", id)}
                testidPrefix="fiscal-wizard-deduction"
              />

              {/* Statut */}
              <div>
                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Statut des déclarations</h4>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.last_declaration_filed}
                      onChange={(e) => setForm({ ...form, last_declaration_filed: e.target.checked })}
                      data-testid="fiscal-wizard-last-declaration-filed"
                    />
                    Dernière déclaration remise
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.current_declaration_in_progress}
                      onChange={(e) => setForm({ ...form, current_declaration_in_progress: e.target.checked })}
                      data-testid="fiscal-wizard-current-declaration-in-progress"
                    />
                    Déclaration en cours
                  </label>
                </div>
              </div>

              <label className="block text-sm">
                <span className="text-xs font-semibold text-slate-700">Notes internes</span>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  maxLength={1000}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="fiscal-wizard-notes"
                />
              </label>
            </div>

            <div className="px-5 py-3 border-t bg-slate-50 flex justify-between gap-2">
              <button onClick={() => setStep(1)} className="text-xs text-slate-500 hover:text-slate-700" data-testid="fiscal-wizard-back">
                ← Étape précédente
              </button>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
                <button
                  onClick={submit}
                  disabled={saving}
                  className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                  data-testid="fiscal-wizard-submit"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Créer le dossier
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function WizardCheckboxGrid({ title, items, selected, onToggle, testidPrefix }) {
  const set = new Set(selected || []);
  return (
    <div>
      <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
        {title} <span className="font-normal text-slate-400">({set.size})</span>
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {items.map((it) => (
          <label
            key={it.id}
            className={`flex items-center gap-2 px-2 py-1.5 border rounded cursor-pointer text-xs transition ${
              set.has(it.id) ? "bg-blue-50 border-blue-300 text-blue-900" : "bg-white border-slate-200"
            }`}
            data-testid={`${testidPrefix}-${it.id}`}
          >
            <input
              type="checkbox"
              checked={set.has(it.id)}
              onChange={() => onToggle(it.id)}
            />
            {it.label}
          </label>
        ))}
      </div>
    </div>
  );
}
